import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaEye, FaTh, FaTable } from "react-icons/fa";
import { Button } from "react-bootstrap";
import { request } from "../../../utils/api";
import { toast, ToastContainer } from "react-toastify";
import { AuthContext } from "../../../auth/AuthContext";
import { formatYYYYMMDD, makeError } from "../../../utils";
import AddLabelModal from "../modal/AddLabelModal";
import ViewUserModal from "../modal/ViewUserModal";
import PrintLabelModal from "../modal/PrintLabelModal";
import Loading from "../utilComponent/Loading";
import Pagination from "../utilComponent/Pagination";
import DataTable from "react-data-table-component";
import { showImage } from "../../../utils";
import { error } from "jquery";

const TrackingList = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { date } = useParams();

  const [searchText, setSearchText] = useState("");
  const [trackingList, setTrackingList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [viewMode, setViewMode] = useState("card"); // "card" or "table"

  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);

  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedPrintProduct, setSelectedPrintProduct] = useState(null);

  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 15,
    total: 0,
    last_page: 1,
  });

  const isSuperAdmin = user?.role_key === "super_admin";
  const isAdmin = user?.role_key === "admin";
  const isPrepTeam = user?.role_key === "prep_team";
  const isTrackingTeam = user?.role_key === "tracking_team";
  const isMember = user?.role_key === "member";

  const canSeeTracking = isAdmin || isTrackingTeam || isMember || isSuperAdmin;
  const canSeePrep = isAdmin || isPrepTeam;
  const canSeeUser = isAdmin || isSuperAdmin;

  const handleSearch = (e) => setSearchText(e.target.value.toLowerCase());

  const filteredProducts = trackingList.filter((item) =>
    Object.values(item).some((val) =>
      String(val).toLowerCase().includes(searchText),
    ),
  );

  const getTrackingList = async (page = 1) => {
    try {
      setLoading(true);

      const response = await request({
        url: `labels-date-wise?page=${page}&per_page=${pagination.per_page}&date=${date}&approved=1`,
        method: "GET",
      });

      if (response?.status) {
        setTrackingList(transformData(response.data.items || []));
        setPagination(response.data.pagination);
      }
    } catch (error) {
      toast.error("Failed to load tracking list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getTrackingList(pagination.current_page);
    // eslint-disable-next-line
  }, [pagination.current_page]);

  const transformData = (data = []) =>
    data.flatMap((item) =>
      item.labels.map((label) => ({
        label_id: label.id,
        label_name: label.label_name,
        label_extract_no: label.label_extract_no,
        product_qty: label.product_qty,
        status: label.status,
        label_file: label.label_file,
        label_upload_date: formatYYYYMMDD(label.created_at),

        product_name: label.product?.name,
        product_sku: label.product?.sku,
        product_image: label.product?.images[0]?.url,

        warehouse_name: label.product?.warehouse?.name,
        user_name: label.user?.name,

        raw: label,

        prep_status: label?.prep_status,
        track_status: label?.track_status,
      })),
    );

  const updateLabelStatus = async (row, status, callFor = "prep") => {
    if (!status) return;

    try {
      setStatusLoading(true);

      const fd = new FormData();
      if (callFor === "tracking") {
        fd.append("track_status", status);
      } else if (callFor === "prep") {
        fd.append("prep_status", status);
      }
      await request({
        url: `labels/${row.label_id}`,
        method: "POST",
        data: fd,
      });

      setTrackingList((prev) =>
        prev.map((item) =>
          item.label_id === row.label_id
            ? callFor === "tracking"
              ? { ...item, track_status: status }
              : { ...item, prep_status: status }
            : item,
        ),
      );
    } catch(error) {
      makeError(error, toast);
    } finally {
      setStatusLoading(false);
    }
  };

  const handlePrepStatusChange = (row, status) => {
    if (status === "Printed") {
      setSelectedPrintProduct(row);
      setShowPrintModal(true);
    } else {
      updateLabelStatus(row, status, "prep");
    }
  };

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, current_page: page }));
  };

  const viewFields = [
    { label: "Label Name", key: "label_name" },
    { label: "Extract No", key: "label_extract_no" },
    { label: "Product Name", key: "product_name" },
    { label: "Product SKU", key: "product_sku" },
    canSeeUser && { label: "User", key: "user_name" },
    { label: "Quantity", key: "product_qty" },
    { label: "Upload Date", key: "label_upload_date" },
    { label: "Tracking Status", key: "track_status" },
    { label: "Prep Status", key: "prep_status" },
  ].filter(Boolean);

  // Table columns definition
  const columns = [
    {
      name: "Image",
      cell: (row) => (
        <img
          src={showImage(row.product_image)}
          alt={row.product_name}
          style={{ width: "50px", height: "50px", objectFit: "contain" }}
          className="rounded"
        />
      ),
      width: "80px",
    },
    {
      name: "Product Name",
      selector: (row) => row.product_name || "N/A",
      sortable: true,
      wrap: true,
    },
    {
      name: "SKU",
      selector: (row) => row.product_sku || "N/A",
      sortable: true,
    },
    {
      name: "Label Name",
      selector: (row) => row.label_name || "N/A",
      sortable: true,
    },
    {
      name: "Warehouse",
      selector: (row) => row.warehouse_name || "N/A",
      sortable: true,
    },
    canSeeUser && {
      name: "User",
      selector: (row) => row.user_name || "N/A",
      sortable: true,
    },
    {
      name: "Quantity",
      selector: (row) => row.product_qty || "N/A",
      sortable: true,
      center: true,
    },
    {
      name: "Status",
      selector: (row) => row.status || "N/A",
      sortable: true,
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="d-flex gap-2">
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => {
              setSelectedItem(row);
              setShowViewModal(true);
            }}
            title="View Details"
          >
            <FaEye />
          </button>
        </div>
      ),
      width: "100px",
      center: true,
    },
  ];

  return (
    <>
      <ToastContainer />
      {statusLoading && (
        <div className="fullscreen-loader">
          <Loading />
        </div>
      )}

      <style>
        {`
          .fullscreen-loader {
            position: fixed;
            inset: 0;
            background: rgba(255,255,255,0.7);
            z-index: 9999;
            display: flex;
            align-items: center;
            justify-content: center;
          }
        `}
      </style>

      {showAddModal && (
        <AddLabelModal
          show={showAddModal}
          setShow={setShowAddModal}
          trackingList={trackingList}
          setTrackingList={setTrackingList}
        />
      )}

      <ViewUserModal
        show={showViewModal}
        onClose={() => setShowViewModal(false)}
        data={selectedItem}
        fields={viewFields}
        title="Tracking Details"
      />

      {showPrintModal && selectedPrintProduct && (
        <PrintLabelModal
          show={showPrintModal}
          onClose={() => setShowPrintModal(false)}
          product={selectedPrintProduct}
          onMarkPrinted={(product) => {
            updateLabelStatus(product, "Printed", "prep");
            setShowPrintModal(false);
          }}
        />
      )}

      <div className="container-fluid mt-3">
        <div className="card shadow-sm">
          <div className="card-header d-flex justify-content-between">
            <h6 className="m-0">Tracking Label List</h6>
            <Button size="sm" onClick={() => navigate("/tracking-view")}>
              Back
            </Button>
          </div>

          <div className="card-body">
            <div className="row mb-3">
              <div className="col-md-6">
                <input
                  className="form-control"
                  placeholder="Search label..."
                  value={searchText}
                  onChange={handleSearch}
                />
              </div>
              <div className="col-md-6 d-flex justify-content-end align-items-center gap-2">
                <div className="btn-group" role="group">
                  <button
                    type="button"
                    className={`btn btn-sm ${viewMode === "card" ? "btn-primary" : "btn-outline-primary"}`}
                    onClick={() => setViewMode("card")}
                    title="Card View"
                  >
                    <FaTh /> Card
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${viewMode === "table" ? "btn-primary" : "btn-outline-primary"}`}
                    onClick={() => setViewMode("table")}
                    title="Table View"
                  >
                    <FaTable /> Table
                  </button>
                </div>
              </div>
            </div>
            {loading ? (
              <Loading />
            ) : filteredProducts.length ? (
              <>
                {viewMode === "card" ? (
                  <div className="row">
                    {filteredProducts.map((item) => (
                      <div key={item.label_id} className="col-3 mb-4">
                        <div className="card h-100 shadow-sm">
                          <img
                            src={showImage(item.product_image)}
                            alt={item.product_name}
                            className="card-img-top"
                            style={{
                              width: "100%",
                              height: "260px",
                              objectFit: "cover",
                            }}
                          />

                          <div className="card-body">
                            <div className="d-flex justify-content-between mb-2">
                              <h6 className="fw-bold">{item.product_name}</h6>
                              <button
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => {
                                  setSelectedItem(item);
                                  setShowViewModal(true);
                                }}
                              >
                                <FaEye />
                              </button>
                            </div>
                            <p className="mb-1">
                              <b>SKU:</b> {item.product_sku}
                            </p>
                            <p className="mb-1">
                              <b>Label:</b> {item.label_name}
                            </p>
                            <p className="mb-1">
                              <b>Warehouse:</b> {item.warehouse_name}
                            </p>
                            <p className="mb-1">
                              <b>Prep Status:</b> {item.prep_status}
                            </p>
                            <p className="mb-1">
                              <b>Track Status:</b> {item.prep_status}
                            </p>

                            {canSeeUser && (
                              <p className="mb-1">
                                <b>User:</b> {item.user_name}
                              </p>
                            )}
                            <p className="mb-2">
                              <b>Qty:</b> {item.product_qty}
                            </p>

                            {canSeeTracking && (
                              <select
                                className="form-select form-select-sm mb-2"
                                value={item.track_status || ""}
                                onChange={(e) =>
                                  updateLabelStatus(
                                    item,
                                    e.target.value,
                                    "tracking",
                                  )
                                }
                              >
                                <option value="">Tracking Status</option>
                                <option value="Label Created">
                                  Label Created
                                </option>
                                <option value="Shipped">Shipped</option>
                                <option value="On the way">On the way</option>
                                <option value="Delivered">Delivered</option>
                                <option value="Not Shipped">Delivered</option>
                                <option value="Out of Stock">
                                  Out of Stock
                                </option>
                              </select>
                            )}
                            {canSeePrep && (
                              <select
                                className="form-select form-select-sm"
                                value={item.prep_status || ""}
                                onChange={(e) =>
                                  handlePrepStatusChange(item, e.target.value)
                                }
                              >
                                <option value="">Prep Status</option>
                                <option value="Printed">Printed</option>
                                <option value="Scanned">Scanned</option>
                                <option value="Updated">Updated</option>
                                <option value="Out of Stock">
                                  Out of Stock
                                </option>
                              </select>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="table-responsive">
                    <DataTable
                      columns={[
                        ...columns,
                        ...(canSeeTracking
                          ? [
                              {
                                name: "Tracking Status",
                                cell: (row) => (
                                  <select
                                    className="form-select form-select-sm"
                                    value={row.track_status || ""}
                                    onChange={(e) =>
                                      updateLabelStatus(
                                        row,
                                        e.target.value,
                                        "tracking",
                                      )
                                    }
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <option value="">Select Status</option>
                                    <option value="Label Created">
                                      Label Created
                                    </option>
                                    <option value="Shipped">Shipped</option>
                                    <option value="On the way">
                                      On the way
                                    </option>
                                    <option value="Delivered">Delivered</option>
                                    <option value="Out of Stock">
                                      Out of Stock
                                    </option>
                                  </select>
                                ),
                                width: "180px",
                              },
                            ]
                          : []),
                        ...(canSeePrep
                          ? [
                              {
                                name: "Prep Status",
                                cell: (row) => (
                                  <select
                                    className="form-select form-select-sm"
                                    value={row.prep_status || ""}
                                    onChange={(e) =>
                                      handlePrepStatusChange(
                                        row,
                                        e.target.value,
                                      )
                                    }
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <option value="">Select Status</option>
                                    <option value="Printed">Printed</option>
                                    <option value="Scanned">Scanned</option>
                                    <option value="Updated">Updated</option>
                                    <option value="Out of Stock">
                                      Out of Stock
                                    </option>
                                  </select>
                                ),
                                width: "180px",
                              },
                            ]
                          : []),
                      ]}
                      data={filteredProducts}
                      defaultSortField="product_name"
                      pagination={false}
                      highlightOnHover
                      className="table table-hover align-middle"
                      noDataComponent={
                        <div className="text-center py-4">
                          {searchText
                            ? "No matching label found"
                            : "No label available"}
                        </div>
                      }
                    />
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-5">
                {searchText ? "No matching label found" : "No label available"}
              </div>
            )}

            {pagination.last_page > 1 && (
              <div className="d-flex justify-content-end mt-4">
                <Pagination
                  currentPage={pagination.current_page}
                  lastPage={pagination.last_page}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default TrackingList;
